import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type DirectUploadZoneContextValue = {
  activate: (element: HTMLElement) => () => void;
  element: HTMLElement | null;
};

/** Sentinel so the hooks below can tell a missing provider from a provider holding no claimed zone. */
const defaultContextValue: DirectUploadZoneContextValue = {
  activate: () => () => {},
  element: null,
};

const DirectUploadZoneContext = createContext<DirectUploadZoneContextValue>(defaultContextValue);

const useDirectUploadZoneContext = (hookName: string) => {
  const context = useContext(DirectUploadZoneContext);

  if (context === defaultContextValue) {
    const error = new Error(`${hookName} must be used within DirectUploadZoneProvider`);

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error.message,
      });
    }
  }

  return context;
};

export type DirectUploadZoneProviderProps = {
  children: ReactNode;
};

export const DirectUploadZoneProvider = memo(({ children }: DirectUploadZoneProviderProps) => {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const activate = useCallback((claimedElement: HTMLElement) => {
    setElement(claimedElement);

    return () => setElement((current) => (current === claimedElement ? null : current));
  }, []);

  const value = useMemo(() => ({ activate, element }), [activate, element]);

  return <DirectUploadZoneContext.Provider value={value}>{children}</DirectUploadZoneContext.Provider>;
});

DirectUploadZoneProvider.displayName = 'DirectUploadZoneProvider';

/**
 * The element a mounted surface has claimed drops for, which doubles as the drag overlay's geometry
 * so the overlay covers the same space the user sees rather than an approximation of it.
 */
export const useDirectUploadZoneElement = () => useDirectUploadZoneContext('useDirectUploadZoneElement').element;

/**
 * Returns a function that claims drops and file picks for the current board, taking the element
 * whose bounds the drag affordance should match. Call it on mount; it returns the cleanup.
 */
export const useActivateDirectUploadZone = () => useDirectUploadZoneContext('useActivateDirectUploadZone').activate;
